import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeart as solidHeart } from "@fortawesome/free-solid-svg-icons";
import { faHeart as outlineHeart } from "@fortawesome/free-regular-svg-icons";
import { likeItem, unlikeItem } from "../../api/itemApi";
import { getFileType, is3DFile } from "../../utils/modelUtils";
import { fetchModelByUrl } from "../../api/modelApi";
import { useDispatch } from "react-redux";
import { addToCart } from "../../redux/actions";
import { PlusCircle } from "lucide-react";

const ClickMeRibbon = () => {
  return (
    <div className="click-me-ribbon">
      click me
      <div className="click-me-arrow"></div>
    </div>
  );
};

const ImagePreview = ({ modelURL, imageSrc, handleViewAR, flatImageSrc }) => {
  if (flatImageSrc && modelURL) {
    return (
      <div className="image-preview-container">
        <ClickMeRibbon />
        <img
          src={flatImageSrc}
          className="menu-img-preview clickable"
          alt="model preview"
          onClick={handleViewAR}
        />
      </div>
    );
  }

  if (modelURL) {
    return (
      <div className="fill-img-preview">
        <button className="CustomerMenuItem-ar-button" onClick={handleViewAR}>
          View in AR
        </button>
      </div>
    );
  }

  return (
    <img src={flatImageSrc} className="menu-img-preview" alt="model preview" />
  );
};

/**
 * A component that displays a menu item, including a logo, name, description, and price.
 *
 * @component
 * @example
 * <CustomerMenuItem
 *    id={2}
 *    photo="/path/to/photo.jpg"
 *    name="Spaghetti Bolognese"
 *    description="Classic Italian pasta with beef and tomato sauce"
 *    price={12.99}
 *    likes={192}
 * />
 *
 * @param {Object} props - The props for the component.
 * @param {number} props.id = The id of the menu item.
 * @param {string} props.photo - The image URL for the menu item.
 * @param {string} props.name - The name of the menu item.
 * @param {string} props.description - The description of the menu item.
 * @param {number} props.price - The price of the menu item.
 * @param {number} props.likes - The number of likes the menu item has.
 * @param {string} [props.modelURL] - The URL for the 3D model associated with the menu item (optional).
 * @param {string} [props.type] - The type for the AR model (.glb) (optional).
 * @returns {JSX.Element} The rendered CustomerMenuItem component.
 */
const CustomerMenuItem = ({
  id,
  photo,
  name,
  description,
  price,
  likes,
  modelURL,
  flatModelUrl,
  type,
}) => {
  const [imageSrc, setImageSrc] = useState("");
  const [flatImageSrc, setFlatImageSrc] = useState("");
  const dispatch = useDispatch();

  useEffect(() => {
    if (modelURL && !is3DFile(modelURL)) {
      const result = fetchModelByUrl(modelURL);
      result.then((data) => {
        setImageSrc(URL.createObjectURL(data));
      });
    } else if (flatModelUrl) {
      const result = fetchModelByUrl(flatModelUrl);
      result.then((data) => {
        setFlatImageSrc(URL.createObjectURL(data));
      });
    }
  }, []);

  const handleViewAR = () => {
    if (modelURL) {
      const type = getFileType(modelURL);
      const arViewerUrl = `/view-ar?model=${encodeURIComponent(
        modelURL
      )}&type=${type}`;
      const arViewerV2Url = `/view-ar-v2?model=${encodeURIComponent(
        modelURL
      )}&type=${type}`;
      //window.location.href = arViewerUrl;
      window.location.href = arViewerV2Url;
    } else {
      alert("No 3D model available for this item.");
    }
  };

  // retrieve liked items from localStorage
  const getLikedItems = () => {
    const savedLikes = localStorage.getItem("likedItems");
    return savedLikes ? JSON.parse(savedLikes) : [];
  };

  // initialized liked state based on whether the item has been liked or not
  const [liked, setLiked] = useState(() => {
    return getLikedItems().includes(id);
  });

  const [numLikes, setLikes] = useState(likes);

  const handleLike = async () => {
    setLikes(numLikes + 1);
    try {
      await likeItem(id);
    } catch (error) {
      console.error("Error liking item:", error.message);
    }
  };

  const handleUnlike = async () => {
    if (numLikes > 0) {
      setLikes(numLikes - 1);
    }
    try {
      await unlikeItem(id);
    } catch (error) {
      console.error("Error unliking item:", error.message);
    }
  };

  const handleLikeButton = async () => {
    const likedItems = getLikedItems();
    let newLikedItems;

    // when liking/unliking an item: update the like count, localStorage, and backend
    if (!liked) {
      newLikedItems = [...likedItems, id];
      await handleLike();
    } else {
      newLikedItems = likedItems.filter((itemId) => itemId !== id);
      await handleUnlike();
    }

    localStorage.setItem("likedItems", JSON.stringify(newLikedItems));
    setLiked(!liked); // toggle the liked status
  };

  // truncates 1000 to 1k, 1000000 to 1m, and 1000000000 to 1b
  const formatLikes = (num) => {
    return new Intl.NumberFormat("en-US", {
      notation: "compact",
      maximumFractionDigits: 1,
    })
      .format(num)
      .toLowerCase();
  };

  const contains3DModel = modelURL && is3DFile(modelURL);
  return (
    <div className="CustomerMenuItem">
      <div className="CustomerMenuItem-main-info">
        <div className="CustomerMenuItem-name-and-description">
          <h1 className="CustomerMenuItem-name">{name}</h1>
          <p className="CustomerMenuItem-description">{description}</p>
          <p className="CustomerMenuItem-price">${price}</p>
        </div>
      </div>
      <div className="CustomerMenuItem-footer">
        <div className="item-photo-container">
          <ImagePreview
            modelURL={modelURL}
            flatImageSrc={flatImageSrc}
            imageSrc={imageSrc}
            handleViewAR={handleViewAR}
          />
          <button
            className="floating-add-button"
            onClick={() => dispatch(addToCart({ id, name, price }, 1))}
            title="Add to cart"
          >
            <PlusCircle size={24} />
          </button>
        </div>
        <div className="CustomerMenuItem-top-hits">
          <button
            className="CustomerMenuItem-like-btn"
            onClick={handleLikeButton}
          >
            <FontAwesomeIcon icon={liked ? solidHeart : outlineHeart} />
          </button>
          <p className="CustomerMenuItem-likes">
            {formatLikes(numLikes)}
            <br />
            <span className="CustomerMenuItem-like-text">
              {numLikes === 1 ? "like" : "likes"}
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default CustomerMenuItem;
